import _ from 'lodash';
import { CreateArchiveSpecification } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/create-archive-specification';
import { Job } from '@wix/media-platform-js-client/dist/mjs/src/platform/management/job/job';

import { getSelectedOrganizationId } from '../../../selectors/organizations/organizations';
import { jobsService } from '../../../transport/services/jobs';
import { Project } from '../../../types/entities';
import { errorLogger } from '../../../utils/error-logger/error-logger';
import { callWithRetry } from '../../../utils/promise';
import { BI_ERRORS } from '../../../utils/statistics/bi-error-logger/bi-errors';
import {
  JobEventPayload,
  jobProcessingFailed,
  jobStarted,
  jobStartFailed,
  jobStatusChanged,
} from '../../jobs/jobs.actions';
import { JobStatus } from '../../jobs/jobs.types';
import { getProjectByRoute } from '../../projects/projects.selectors';
import { StoreState } from '../../reducers/store.types';
import { Dispatch } from '../../redux.types';

function isJobInProgress<ResultPayload>(
  job: Job<CreateArchiveSpecification>,
): boolean {
  return job.status !== JobStatus.SUCCESS && job.status !== JobStatus.ERROR;
}

const waitForJobToComplete =
  (dispatch: Dispatch) =>
  (context: any, organizationId: string, project: Project) =>
  (
    job: Job<CreateArchiveSpecification>,
  ): Promise<Job<CreateArchiveSpecification>> => {
    if (!isJobInProgress(job)) {
      return Promise.resolve(job);
    }

    let savedJobStatus = job.status;

    return callWithRetry<Job<CreateArchiveSpecification>>(
      (): Promise<Job<CreateArchiveSpecification>> => {
        // get latest job and if status has changed than dispatch action to notify about updates
        return jobsService
          .getJob(organizationId, project, job.id)
          .then((latestJob) => {
            if (latestJob.status !== savedJobStatus) {
              savedJobStatus = latestJob.status;
              dispatch(
                jobStatusChanged({
                  context,
                  fileId: _.get(latestJob, 'result.payload.id'),
                  job: latestJob,
                }),
              );
            }
            return latestJob;
          })
          .catch((error) => {
            errorLogger.logError({
              errorDescription: BI_ERRORS.jobs.jobStatusCheckTryFailed,
              otherParams: error,
            });
            return Promise.reject(error);
          });
      },
      {
        retryInterval: 5000,
        maxTries: Number.POSITIVE_INFINITY,
        maxTriesForUnknownFailures: 3, // wait infinite time till job is complete but in case of some unknown error fall after 3 times
        isBadResultFn: isJobInProgress,
      },
    ).catch((error) => {
      errorLogger.logError({
        errorDescription: BI_ERRORS.jobs.jobStatusCheckFailed,
      });
      return Promise.reject(error);
    });
  };

export const jobsMiddleware =
  ({ dispatch, getState }: any) =>
  (next) =>
  (action) => {
    const { initiateJob } = action;

    if (!initiateJob) {
      // Not a 'job' action: pass it on
      return next(action);
    }

    if (typeof initiateJob !== 'function') {
      throw new Error('Expected initiateJob to be a function.');
    }

    const { context } = action;
    const state: StoreState = getState();
    const organizationId = getSelectedOrganizationId(state);
    const project = getProjectByRoute(state);

    return initiateJob()
      .then(
        (job) => {
          dispatch(jobStarted({ job, context }));
          return job;
        },
        (error) => {
          dispatch(jobStartFailed({ error, context }));
          return Promise.reject(error);
        },
      )
      .then((job) =>
        waitForJobToComplete(dispatch)(
          context,
          organizationId,
          project,
        )(job).catch((error) => {
          dispatch(jobProcessingFailed({ job, error, context }));
          return Promise.reject(error);
        }),
      )
      .then((job) => {
        const result: JobEventPayload<any> = { job, context };
        return result;
      });
  };
